import { motion } from 'motion/react'
import {
  Calendar, ArrowRight, BookOpen,
  Scale, Gamepad2, Wrench,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { MarketingLayout } from '../components/MarketingLayout'
import { SEO } from '../components/SEO'
import { CTABanner } from '../components/CTABanner'

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Voltary'

// Cada item del hub apunta a una página existente o futura.
// published=false → "Próximamente" sin link
type Section = 'comparativas' | 'guias-juego' | 'tutoriales'

interface HubItem {
  slug: string
  title: string
  description: string
  href: string
  badge?: string
  published: boolean
  /** Solo para entradas tipo tutorial: fecha ISO y tiempo de lectura */
  date?: string
  readMinutes?: number
}

interface HubSection {
  id: Section
  title: string
  description: string
  icon: React.ReactNode
  items: HubItem[]
}

const SECTIONS: HubSection[] = [
  {
    id: 'comparativas',
    title: 'Comparativas de hosting',
    description: '¿Cómo se compara Voltary frente a otras opciones del mercado?',
    icon: <Scale size={18} />,
    items: [
      {
        slug: 'vs-hosting-mensual',
        title: 'Voltary vs hosting de pago mensual',
        description: 'Comparamos el modelo de pago por horas contra suscripciones mensuales del mercado.',
        href: '/vs/hosting-mensual',
        badge: 'Mensual',
        published: true,
      }
    ],
  },
  {
    id: 'guias-juego',
    title: 'Guías por juego',
    description: 'Todo lo que necesitas saber para montar tu servidor del juego que quieras.',
    icon: <Gamepad2 size={18} />,
    items: [
      {
        slug: 'minecraft',
        title: 'Hosting de Minecraft',
        description: 'Vanilla, Forge, Fabric, Spigot. Cualquier versión, cualquier modpack.',
        href: '/minecraft',
        badge: 'Java',
        date: '2026-08-14',
        published: true,
      },
      {
        slug: 'counter-strike',
        title: 'Hosting de Counter-Strike 1.6 y Source',
        description: 'Tickrate 100 estable, AMX Mod X, anti-cheat. Hasta 32 jugadores.',
        href: '/counter-strike',
        badge: 'CS 1.6 / CS:S',
        date: '2026-08-14',
        published: true,
      },
      {
        slug: 'terraria',
        title: 'Hosting de Terraria',
        description: 'Servidores dedicados para Terraria. Configura tu mundo en minutos.',
        href: '/terraria',
        badge: 'Próximamente',
        published: false,
      },
    ],
  },
  {
    id: 'tutoriales',
    title: 'Tutoriales y guías',
    description: 'Pasos concretos para crear, configurar y optimizar tu servidor.',
    icon: <Wrench size={18} />,
    items: [
      {
        slug: 'como-crear-servidor-minecraft',
        title: 'Cómo crear un servidor de Minecraft en 2026',
        description: 'Paso a paso: requisitos de RAM, configuración y primeros pasos.',
        href: '/blog/como-crear-servidor-minecraft',
        published: true,
        date: '2026-08-14',
        readMinutes: 8,
      },
      {
        slug: 'cuanta-ram-necesita-minecraft',
        title: '¿Cuánta RAM necesita un servidor de Minecraft?',
        description: 'La fórmula para calcular RAM según jugadores, mods y plugins.',
        href: '/blog/cuanta-ram-necesita-minecraft',
        published: false,
        date: '2026-08-21',
        readMinutes: 6,
      },
      {
        slug: 'como-instalar-plugins-minecraft',
        title: 'Cómo instalar plugins en tu servidor de Minecraft',
        description: 'Spigot, Paper, Bukkit: diferencias y cómo subir plugins sin romper el servidor.',
        href: '/blog/como-instalar-plugins-minecraft',
        published: false,
        date: '2026-08-28',
        readMinutes: 7,
      },
    ],
  },
]

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', {
    day: 'numeric', month: 'long', year: 'numeric',
  })
}

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: `Blog de ${APP_NAME}`,
  description: 'Centro de recursos: comparativas de hosting, guías por juego y tutoriales.',
}

export function BlogIndex() {
  const totalPublished = SECTIONS.reduce(
    (acc, s) => acc + s.items.filter((i) => i.published).length,
    0
  )

  return (
    <MarketingLayout>
      <SEO
        title={`Blog de ${APP_NAME} — Comparativas, guías y tutoriales`}
        description="Centro de recursos sobre hosting de servidores de juegos. Comparativas con suscripciones mensuales, guías para Minecraft y Counter-Strike, y tutoriales paso a paso."
        canonical="https://voltary.org/blog"
        jsonLd={jsonLd}
      />

      <section className="marketing-blog">
        <motion.div
          className="marketing-blog-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="marketing-article-eyebrow">
            <BookOpen size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Centro de recursos
          </span>
          <h1 className="marketing-blog-title">
            Todo sobre hosting de servidores de juegos
          </h1>
          <p className="marketing-blog-lead">
            Comparativas, guías y tutoriales para ayudarte a decidir y montar
            tu servidor sin perder horas en pruebas y errores.
          </p>
        </motion.div>

        {SECTIONS.map((section, sIdx) => (
          <section
            key={section.id}
            className="marketing-blog-section"
            aria-labelledby={`section-${section.id}`}
          >
            <motion.div
              className="marketing-blog-section-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5 }}
            >
              <div className="marketing-blog-section-icon">{section.icon}</div>
              <div>
                <h2 id={`section-${section.id}`}>{section.title}</h2>
                <p>{section.description}</p>
              </div>
            </motion.div>

            <div className="marketing-blog-grid">
              {section.items.map((item, idx) => {
                const card = (
                  <motion.article
                    className={`marketing-blog-card${item.published ? '' : ' marketing-blog-card--soon'}`}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ duration: 0.4, delay: idx * 0.05 }}
                  >
                    <div className="marketing-blog-card-meta">
                      {item.badge && (
                        <span className="marketing-blog-category">{item.badge}</span>
                      )}
                      {!item.published && (
                        <span className="marketing-blog-soon-tag">Próximamente</span>
                      )}
                    </div>

                    <h3 className="marketing-blog-card-title">{item.title}</h3>
                    <p className="marketing-blog-card-desc">{item.description}</p>

                    <div className="marketing-blog-card-footer">
                      {item.date && (
                        <span className="marketing-blog-date">
                          <Calendar size={12} /> {formatDate(item.date)}
                        </span>
                      )}
                      {item.published && (
                        <span className="marketing-blog-arrow">
                          Leer <ArrowRight size={14} />
                        </span>
                      )}
                    </div>
                  </motion.article>
                )

                return item.published ? (
                  <Link key={item.slug} to={item.href} className="marketing-blog-card-link">
                    {card}
                  </Link>
                ) : (
                  <div key={item.slug} className="marketing-blog-card-link">
                    {card}
                  </div>
                )
              })}
            </div>
          </section>
        ))}

        {totalPublished === 0 && (
          <p className="marketing-blog-empty">
            Aún no hemos publicado el primer artículo. Vuelve en unos días.
          </p>
        )}
      </section>

      <CTABanner
        title="Después de leer, monta tu servidor"
        description={`Tienes ${totalPublished} recurso(s) ya disponible(s).`}
        primaryLabel="Empezar ahora"
        primaryHref="/login"
      />
    </MarketingLayout>
  )
}